import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import GoBackButton from "../GoBackButton";
import { ROUTES } from "../../constants";
import { useNavigationOrigin } from "../../contexts";
import {
  ContentLayout,
  LayoutContainer,
  GoBackContainer,
  MainContainer,
} from "./Layout.styled";

type LayoutProps = {
  children: React.ReactNode;
};

// Get parent route for edit pages (always go to their view page)
const getEditPageParent = (pathname: string): string | undefined => {
  if (pathname.match(/^\/treatment-plan\/\d+\/edit$/)) {
    const id = pathname.match(/^\/treatment-plan\/(\d+)\/edit$/)?.[1];
    return id ? `/treatment-plan/${id}` : undefined;
  }

  if (pathname.match(/^\/user-management\/\d+\/edit$/)) {
    const id = pathname.match(/^\/user-management\/(\d+)\/edit$/)?.[1];
    return id ? `/user-management/${id}` : undefined;
  }

  if (pathname === "/profile/edit") {
    return "/profile";
  }

  return undefined;
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const { getOrigin } = useNavigationOrigin();

  const shouldShowGoBack = location.pathname !== ROUTES.DASHBOARD;

  const parentRoute = useMemo(() => {
    const pathname = location.pathname;

    // For edit pages, always go to their view page
    const editParent = getEditPageParent(pathname);
    if (editParent) {
      return editParent;
    }

    // For view pages and other pages, use tracked origin
    return getOrigin(pathname);
  }, [location.pathname, getOrigin]);

  return (
    <LayoutContainer>
      <Sidebar />
      <ContentLayout>
        <MainContainer>
          {shouldShowGoBack && (
            <GoBackContainer>
              <GoBackButton to={parentRoute} />
            </GoBackContainer>
          )}
          {children}
        </MainContainer>
      </ContentLayout>
    </LayoutContainer>
  );
};

export default Layout;
